/**
 * Křídová náhledová plocha produktu (tabulové SVG dle předmětu/typu).
 * Bespoke glyphy pro math/physics + VIDEO/APP; pro ostatní předměty generický
 * křídový fallback (lucide ikona předmětu + akcent v barvě jeho křídy).
 *
 * Seedovaná variace (proposal 003 §3.4): 3 varianty glyfu na předmět dle `productId`,
 * plus jemná rotace a posun, aby žádné dvě karty nebyly pixelově stejné.
 *
 * Draw-in (proposal 003 §6.5): s `animateDraw` se tahy dokreslí (pathLength 0 -> 1),
 * akcent poslední. Bez `animateDraw` je render okamžitý a statický (nula regrese
 * pro detail, pokladnu a odpočívající kartu): motion prvky bez aktivní varianty
 * kreslí naplno.
 */

import type { ComponentProps } from 'react';
import { motion } from 'framer-motion';
import type { ChalkColor, ContentType, SubjectRef } from '../lib/api';
import { chalkHex, subjectIcon } from '../lib/format';
import { EASE } from '../lib/motion';
import { seed } from '../lib/seed';

const CHALK_FILL = '#F3EFE4';
const serif = "'DM Serif Display',serif";

/** Neutrální předmět (křídová bílá + ikona `grad`) pro chybějící/poškozená data. */
const SUBJECT_FALLBACK: SubjectRef = {
  slug: '',
  name: '',
  icon: 'grad',
  color: 'white',
};

/* Draw-in varianty: tahy pathLength, výplně/text opacity, kontejner staguje. */
const strokeV = {
  hidden: { pathLength: 0, opacity: 0 },
  visible: {
    pathLength: 1,
    opacity: 1,
    transition: { pathLength: { duration: 0.5, ease: EASE }, opacity: { duration: 0.12 } },
  },
};
const fillV = {
  hidden: { opacity: 0 },
  visible: { opacity: 1, transition: { duration: 0.25, ease: EASE } },
};
const containerV = {
  hidden: {},
  visible: { transition: { staggerChildren: 0.06 } },
};

// Pevné motion wrappery (bez aktivní varianty se vykreslí naplno).
const MPath = (p: ComponentProps<typeof motion.path>) => <motion.path variants={strokeV} {...p} />;
const MCircle = (p: ComponentProps<typeof motion.circle>) => (
  <motion.circle variants={strokeV} {...p} />
);
const MRect = (p: ComponentProps<typeof motion.rect>) => <motion.rect variants={strokeV} {...p} />;
const MFill = (p: ComponentProps<typeof motion.path>) => <motion.path variants={fillV} {...p} />;
const MText = (p: ComponentProps<typeof motion.text>) => <motion.text variants={fillV} {...p} />;

/* Glyphy (varianty dle seedu) */

function MathGlyph({ accent, variant }: { accent: string; variant: number }) {
  if (variant === 1) {
    // Osa x/y s parabolou a „y = x²".
    return (
      <>
        <MPath d="M30 96 h108" />
        <MPath d="M46 108 v-84" />
        <MPath d="M60 92 Q92 8 126 92" stroke={accent} strokeWidth={2.6} />
        <MText x="120" y="34" fontFamily={serif} fontSize="17" fill={CHALK_FILL} stroke="none">
          y = x²
        </MText>
      </>
    );
  }
  if (variant === 2) {
    // Pravoúhlý trojúhelník s „a² + b² = c²".
    return (
      <>
        <MPath d="M44 92 h72 l-72 -54 Z" />
        <MPath d="M44 80 h12 v12" stroke={accent} strokeWidth={2} />
        <MText x="30" y="112" fontFamily={serif} fontSize="15" fill={CHALK_FILL} stroke="none">
          a² + b² = c²
        </MText>
      </>
    );
  }
  // Koláčový graf + zlomek.
  return (
    <>
      <MCircle cx="52" cy="60" r="26" />
      <MFill
        d="M52 60 L52 34 A26 26 0 0 1 78 60 Z"
        fill={accent}
        stroke={accent}
        fillOpacity={0.85}
      />
      <MPath d="M52 60 v-26 M52 60 h26" />
      <MText x="104" y="52" fontFamily={serif} fontSize="24" fill={CHALK_FILL} stroke="none">
        3
      </MText>
      <MPath d="M98 60 h26" />
      <MText x="104" y="86" fontFamily={serif} fontSize="24" fill={CHALK_FILL} stroke="none">
        4
      </MText>
      <MText x="134" y="70" fontFamily={serif} fontSize="22" fill={accent} stroke="none">
        = ?
      </MText>
    </>
  );
}

function PhysicsGlyph({ accent, variant }: { accent: string; variant: number }) {
  if (variant === 1) {
    // Kladka se závažím.
    return (
      <>
        <MCircle cx="90" cy="34" r="16" />
        <MCircle cx="90" cy="34" r="3" fill={CHALK_FILL} />
        <MPath d="M74 34 v40 M106 34 v20" />
        <MRect x="62" y="74" width="24" height="18" rx="2" stroke={accent} />
        <MPath d="M100 54 l6 8 -12 0 Z" stroke={accent} />
      </>
    );
  }
  if (variant === 2) {
    // Jednoduchý obvod se žárovkou a proudem I.
    return (
      <>
        <MRect x="40" y="34" width="100" height="56" rx="4" />
        <MCircle cx="90" cy="34" r="9" stroke={accent} />
        <MPath d="M84 30 l12 8 M96 30 l-12 8" stroke={accent} />
        <MPath d="M40 62 h10 M130 62 h10" />
        <MText
          x="112"
          y="82"
          fontFamily={serif}
          fontStyle="italic"
          fontSize="15"
          fill={accent}
          stroke="none"
        >
          I
        </MText>
      </>
    );
  }
  // Odporový obvod + „U = R · I".
  return (
    <>
      <MPath d="M40 40 h100 v50 h-100 Z" />
      <MPath d="M80 84 h8 M76 96 h16" />
      <MCircle cx="140" cy="40" r="11" stroke={accent} />
      <MPath d="M133 33 l14 14 M147 33 l-14 14" stroke={accent} />
      <MText
        x="46"
        y="28"
        fontFamily={serif}
        fontStyle="italic"
        fontSize="16"
        fill={CHALK_FILL}
        stroke="none"
      >
        U = R · I
      </MText>
      <MPath d="M60 62 l10 8 -6 2 6 10" stroke={accent} strokeWidth={2} />
    </>
  );
}

function VideoGlyph({ accent, variant }: { accent: string; variant: number }) {
  const eqs = ['2x + 3 = 11', 'v = s / t', 'sin α = a / c'];
  return (
    <>
      <MCircle cx="90" cy="58" r="30" />
      <MFill d="M83 46 l20 12 -20 12 Z" fill={accent} stroke={accent} />
      <MText
        x="24"
        y="24"
        fontFamily={serif}
        fontStyle="italic"
        fontSize="16"
        fill={CHALK_FILL}
        stroke="none"
      >
        {eqs[variant] ?? eqs[0]}
      </MText>
      <MPath d="M132 96 c10 -6 22 -6 30 0" opacity={0.55} />
    </>
  );
}

function AppGlyph({ accent }: { accent: string }) {
  return (
    <>
      <MRect x="46" y="30" width="88" height="58" rx="6" />
      <MPath d="M46 44 h88" opacity={0.6} />
      <MCircle cx="54" cy="37" r="1.6" fill={CHALK_FILL} />
      <MCircle cx="60" cy="37" r="1.6" fill={CHALK_FILL} />
      <MPath d="M64 66 l10 -8 10 8" stroke={accent} />
      <MCircle cx="104" cy="60" r="8" stroke={accent} />
      <MPath d="M60 102 h60" opacity={0.5} />
    </>
  );
}

/** Generický křídový glyph pro nové předměty: velká lucide ikona + akcent v barvě křídy. */
function GenericGlyph({ subject, accent }: { subject: SubjectRef; accent: string }) {
  const Icon = subjectIcon(subject.icon ?? 'grad');
  return (
    <>
      <Icon
        x={62}
        y={22}
        width={56}
        height={56}
        color={CHALK_FILL}
        strokeWidth={1.7}
        fill="none"
        opacity={0.95}
      />
      <MPath d="M54 94 h72" stroke={accent} strokeWidth={3} strokeLinecap="round" />
      <MCircle cx="132" cy="30" r="4.5" fill={accent} stroke={accent} />
    </>
  );
}

export default function ProductPreview({
  subject,
  contentType,
  productId = 0,
  animateDraw = false,
  className,
}: {
  subject: SubjectRef | null | undefined;
  contentType: ContentType;
  /** Pro seedovanou variaci glyfu (stabilní dle produktu). */
  productId?: number;
  /** Dokreslovací mikroanimace na hover/focus (proposal 003 §6.5). */
  animateDraw?: boolean;
  className?: string;
}) {
  // Defenzivně: chybějící/poškozený předmět → neutrální náhrada (render nespadne).
  const safeSubject = subject ?? SUBJECT_FALLBACK;
  const accent = chalkHex((safeSubject.color ?? 'white') as ChalkColor);
  const variant = seed(productId, 3);

  let glyph = <GenericGlyph subject={safeSubject} accent={accent} />;
  if (contentType === 'VIDEO') glyph = <VideoGlyph accent={accent} variant={variant} />;
  else if (contentType === 'APP') glyph = <AppGlyph accent={accent} />;
  else if (safeSubject.icon === 'physics')
    glyph = <PhysicsGlyph accent={accent} variant={variant} />;
  else if (safeSubject.icon === 'math') glyph = <MathGlyph accent={accent} variant={variant} />;

  // Jemná, deterministická rotace a posun (do 1.6°, do 4px), ruční sazba, ne chaos.
  const rot = (seed(productId, 5) - 2) * 0.8;
  const dx = (seed(productId, 3) - 1) * 4;
  const style = { transform: `rotate(${rot}deg) translateX(${dx}px)` };

  const groupProps = {
    stroke: CHALK_FILL,
    strokeWidth: 2.2,
    strokeLinecap: 'round' as const,
    strokeLinejoin: 'round' as const,
    opacity: 0.92,
    fill: 'none' as const,
  };

  return (
    <svg viewBox="0 0 180 120" fill="none" aria-hidden="true" className={className} style={style}>
      {animateDraw ? (
        <motion.g variants={containerV} initial="hidden" animate="visible" {...groupProps}>
          {glyph}
        </motion.g>
      ) : (
        <g {...groupProps}>{glyph}</g>
      )}
    </svg>
  );
}
